import { useRef, useState } from 'react'
import { isAxiosError } from 'axios'
import {
  Badge, Button, Group, Loader, Modal, PasswordInput, Stack, Text,
} from '@mantine/core'
import { IconRefresh } from '@tabler/icons-react'
import { useQueryClient } from '@tanstack/react-query'

import {
  WIFI_ATIVOS, cancelarWifi, pedirWifi, useWifiDoRobo, type EnderecoDoWifi,
  type EstadoDaRedePadrao, type MotivoWifi, type OrdemWifi, type RedeSalva, type RoboFrota,
} from '@/api/robo'
import { ModoRedeSemCabo, temModoRede } from './ModoRedeSemCabo'
import { ReciboDoAviso } from './ReciboDoAviso'
import { rotuloDoRobo } from './avisoDoAjuste'
import { Callout } from '@/components/ui'
import { EscolherRede, type Escolha } from '@/features/robo-wifi/EscolherRede'
import { bytesUtf8, problemaNaRede } from '@/features/robo-wifi/redes'

export const SEM_RELATO =
  'Este robô ainda não informa as redes. Atualize o firmware pelo ar.'

/**
 * Toda recusa do console (`robo/wifi_remoto.py`, `robo_admin_routes.py`) em
 * frase de quem administra. Código novo lá sem frase aqui cai no genérico.
 */
export const RECUSA_WIFI: Record<string, string> = {
  sem_relato: SEM_RELATO,
  ordem_ativa: 'Já há uma ordem de Wi-Fi em andamento para este robô. Espere terminar ou cancele.',
  ultima_rede: 'É a única rede em que o robô já conectou. Sem ela, ele só volta pela USB.',
  ssid_invalido: 'O nome da rede precisa ter de 1 a 32 bytes.',
  senha_invalida: 'A senha passa de 63 bytes, e o robô não aceita.',
  cifra_indisponivel: 'O console está sem a chave de cifra e não grava senha agora. Nada foi enviado ao robô.',
  // Rede padrão (TASK-11996).
  senha_guardada_ilegivel: 'O console não conseguiu ler a senha guardada e não mudou nada. Tente de novo em instantes; se continuar, remova a rede padrão e defina de novo.',
  alteracao_concorrente: 'Outra pessoa alterou a rede padrão agora. Recarregue a página para ver a rede atual antes de mudar.',
  robo_nao_ativo: 'Só robô ativo recebe ordem de Wi-Fi. Reative o robô antes.',
  acao_invalida: 'O console não reconheceu o pedido. Recarregue a página e tente de novo.',
  corpo_invalido: 'O console não reconheceu o pedido. Recarregue a página e tente de novo.',
  sem_ordem_ativa: 'A ordem já tinha terminado.',
  transicao_recusada: 'A ordem já tinha terminado.',
}

function frase(erro: unknown): string {
  const detail = isAxiosError(erro) ? erro.response?.data?.detail : undefined
  return (typeof detail === 'string' && RECUSA_WIFI[detail]) || 'Não foi possível falar com o console.'
}

/** Vocabulário fechado de `robo_wifi_ordem.motivo`. */
export const MOTIVO_WIFI: Record<MotivoWifi, string> = {
  rede: 'a rede não respondeu, e o robô voltou para a senha anterior.',
  formato: 'o robô recusou o nome ou a senha, fora dos limites dele.',
  ultima: 'o robô recusou remover a única rede em que já conectou.',
  expirou: 'o robô não buscou a ordem em 1 hora (desligado ou fora da rede?). A senha foi apagada do console.',
  firmware: 'o firmware do robô não sabe cumprir esta ordem. Atualize o firmware pelo ar.',
}

/** O que a ordem é, e onde ela está — o texto humano da spec 4.3. */
export function textoDaOrdem(o: OrdemWifi): { oque: string; estado: string } {
  const oque = o.acao === 'escanear'
    ? 'Procurar redes'
    : o.acao === 'esquecer' ? `Remover a rede ${o.ssid}` : `Gravar a rede ${o.ssid}`
  if (o.status === 'pendente') return { oque, estado: 'Aguardando o robô buscar a ordem.' }
  if (o.status === 'aplicando') {
    return {
      oque,
      estado: o.acao === 'salvar' ? 'Testando a senha nova.'
        : o.acao === 'escanear' ? 'O robô está procurando redes.' : 'O robô está removendo a rede.',
    }
  }
  if (o.status === 'aplicado') {
    return {
      oque,
      // "aplicado" de rede que não está em uso é "gravada", não "conectado" (spec 3.3).
      estado: o.acao === 'salvar' ? 'Gravada no robô. Ele conecta nela quando estiver ao alcance.'
        : o.acao === 'escanear' ? 'Busca concluída.' : 'Removida do robô.',
    }
  }
  if (o.status === 'cancelado') return { oque, estado: 'Cancelada.' }
  return { oque, estado: `Falhou: ${o.motivo ? MOTIVO_WIFI[o.motivo] : 'o robô não disse por quê.'}` }
}

/** A linha "Rede padrão" do detalhe (spec 2026-09-29-robo-wifi-padrao, 4.2). */
export function textoDaRedePadrao(p: EstadoDaRedePadrao): string {
  switch (p.estado) {
    case 'gravada': return 'Gravada no robô.'
    case 'a_caminho': return 'A caminho: o robô grava na próxima vez que falar com o console.'
    case 'aguardando': return 'Aguardando: o robô recebe em até 5 minutos depois de falar com o console.'
    // Expirada, a senha da rede padrão continua no console e a volta tenta de novo:
    // a frase genérica ("a senha foi apagada") diria o contrário.
    case 'falhou': return p.motivo === 'expirou'
      ? 'Falhou: o robô não buscou a ordem em 1 hora (desligado ou fora da rede?). O console tenta de novo em 24 horas.'
      : `Falhou: ${p.motivo ? MOTIVO_WIFI[p.motivo] : 'o robô não disse por quê.'}`
    case 'dispensada': return 'Removida ou cancelada neste robô. Ela só volta se a rede padrão for trocada ou definida de novo.'
    case 'nao_se_aplica': return 'Não se aplica: este robô não informa as redes.'
  }
}

/** Remover a última confirmada deixa o robô sem rede que já funcionou (spec 3.4). */
export function motivoParaNaoRemover(rede: RedeSalva, redes: readonly RedeSalva[]): string | null {
  const confirmadas = redes.filter((r) => r.confirmada)
  if (rede.confirmada && confirmadas.length === 1) {
    return 'É a única rede em que o robô já conectou. Sem ela, ele só volta pela USB.'
  }
  return null
}

export const AVISO_TROCA_EM_USO =
  'O robô testa a senha nova nesta mesma rede, então isto só funciona enquanto a senha antiga '
  + 'ainda vale. Se a senha do roteador já mudou, o robô precisa de uma segunda rede salva e ao '
  + 'alcance (um roteador de apoio ou o hotspot de um celular): ele cai para ela, volta a falar '
  + 'com o console, e aí se troca a senha desta. Sem segunda rede, só a USB o traz de volta.'

/**
 * A senha nunca entra em estado do React nem em cache do react-query: o campo é
 * não controlado, a tela guarda só o TAMANHO dela para validar, e o valor é
 * lido do campo no envio e apagado dele na mesma hora.
 */
function useCampoDeSenha() {
  const ref = useRef<HTMLInputElement>(null)
  const [bytesDaSenha, setBytes] = useState(0)
  return {
    ref,
    bytesDaSenha,
    aoMudar: (ev: React.ChangeEvent<HTMLInputElement>) => setBytes(bytesUtf8(ev.currentTarget.value)),
    tirar: (): string => {
      const valor = ref.current?.value ?? ''
      if (ref.current) ref.current.value = ''
      setBytes(0)
      return valor
    },
  }
}

type Modo =
  | { tipo: 'nada' }
  | { tipo: 'trocar'; rede: RedeSalva }
  | { tipo: 'remover'; rede: RedeSalva }

/**
 * `endereco` decide por onde o cartão fala: a Frota passa o do robô, o wizard do
 * dono passa o dele (spec 2026-09-27-robo-recebimento, 4.3).
 */
export function WifiDoRobo({ robo, endereco, semSinal = false }: {
  robo: Pick<RoboFrota, 'hardware_model'>
  endereco: EnderecoDoWifi
  /** Robô sem sinal: com a capacidade relatada, o caminho sem cabo vem antes de tudo. */
  semSinal?: boolean
}) {
  const qc = useQueryClient()
  const wifi = useWifiDoRobo(endereco)
  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [modo, setModo] = useState<Modo>({ tipo: 'nada' })
  const [escolha, setEscolha] = useState<Escolha>({ ssid: '', oculta: false })
  const senhaNova = useCampoDeSenha()
  const senhaTroca = useCampoDeSenha()

  if (wifi.isLoading) return <Loader size="sm" />
  if (wifi.isError) {
    return <Text size="sm" c="red.7">Não foi possível consultar o Wi-Fi do robô.</Text>
  }
  const relato = wifi.data?.relato ?? null
  const ordem = wifi.data?.ordem ?? null
  const semCabo = semSinal && temModoRede(relato) ? <ModoRedeSemCabo /> : null
  const ativa = ordem != null && WIFI_ATIVOS.includes(ordem.status)

  const agir = async (acao: () => Promise<unknown>): Promise<boolean> => {
    setEnviando(true)
    setErro(null)
    let ok = false
    try {
      await acao()
      ok = true
    } catch (e) {
      setErro(frase(e))
    } finally {
      setEnviando(false)
      await qc.invalidateQueries({ queryKey: endereco.chave })
    }
    return ok
  }

  const blocoDaOrdem = ordem && (() => {
    const t = textoDaOrdem(ordem)
    return (
      <Stack gap={4}>
        <Text size="sm" fw={600}>{t.oque}</Text>
        <Text size="sm" c={ordem.status === 'falhou' ? 'red.7' : undefined}>{t.estado}</Text>
        {/* "REDE SALVA" sai quando o ROBO aplica, não no pedido: o recibo vem com a ordem. */}
        {ordem.status === 'aplicado' && ordem.aviso && (
          <ReciboDoAviso
            key={ordem.aviso.id ?? `${ordem.id}-${ordem.aviso.estado}`}
            aviso={ordem.aviso}
            caminho={ordem.aviso.id ? endereco.recibo(ordem.aviso.id) : null}
            robo={rotuloDoRobo(robo.hardware_model)}
          />
        )}
        {ativa && (
          <Group>
            <Button variant="default" size="xs" loading={enviando}
              onClick={() => agir(() => cancelarWifi(endereco))}>
              Cancelar ordem
            </Button>
          </Group>
        )}
      </Stack>
    )
  })()

  const padrao = wifi.data?.padrao
  const blocoDaRedePadrao = padrao && (
    <Stack gap={2} data-testid="rede-padrao-no-robo">
      <Text size="sm" fw={600}>Rede padrão dos robôs</Text>
      <Text size="sm" c={padrao.estado === 'falhou' ? 'red.7' : undefined}>
        {textoDaRedePadrao(padrao)}
      </Text>
    </Stack>
  )

  if (!relato) {
    return (
      <Stack gap="sm">
        <Callout variant="info">{SEM_RELATO}</Callout>
        {blocoDaOrdem}
        {blocoDaRedePadrao}
      </Stack>
    )
  }

  const salvas = new Set(relato.redes.map((r) => r.ssid))
  const problema = problemaNaRede(escolha.ssid, senhaNova.bytesDaSenha)
  const motivoOcupado = ativa ? 'Há uma ordem em andamento. Espere terminar ou cancele.' : null

  const salvarNova = async () => {
    const senha = senhaNova.tirar()
    const ok = await agir(() => pedirWifi(endereco, { acao: 'salvar', ssid: escolha.ssid, senha }))
    if (ok) setEscolha({ ssid: '', oculta: false })
  }

  const confirmarTroca = async (rede: RedeSalva) => {
    const senha = senhaTroca.tirar()
    setModo({ tipo: 'nada' })
    await agir(() => pedirWifi(endereco, { acao: 'salvar', ssid: rede.ssid, senha }))
  }

  const confirmarRemocao = async (rede: RedeSalva) => {
    setModo({ tipo: 'nada' })
    await agir(() => pedirWifi(endereco, { acao: 'esquecer', ssid: rede.ssid }))
  }

  return (
    <Stack gap="lg">
      {semCabo}
      {blocoDaOrdem}
      {erro && <Callout variant="warning">{erro}</Callout>}

      <Stack gap="xs">
        <Text size="sm" fw={600}>Redes salvas</Text>
        {relato.redes.length === 0 && (
          <Text size="sm" c="dimmed">O robô não tem nenhuma rede salva.</Text>
        )}
        {relato.redes.map((r) => {
          const naoRemove = motivoParaNaoRemover(r, relato.redes)
          return (
            <Stack key={r.ssid} gap={2} data-testid={`rede-salva-${r.ssid}`}>
              <Group gap="xs" wrap="wrap">
                <Text size="sm" fw={500}>{r.ssid}</Text>
                {r.em_uso && <Badge size="sm" variant="light" color="inovareTeal">Em uso</Badge>}
                {!r.confirmada && <Badge size="sm" variant="light" color="gray">Nunca conectou</Badge>}
                <Button variant="default" size="xs" disabled={enviando || ativa}
                  onClick={() => setModo({ tipo: 'trocar', rede: r })}>
                  Trocar senha
                </Button>
                <Button variant="light" size="xs" color="red" disabled={enviando || ativa || !!naoRemove}
                  onClick={() => setModo({ tipo: 'remover', rede: r })}>
                  Remover
                </Button>
              </Group>
              {naoRemove && <Text size="xs" c="dimmed">{naoRemove}</Text>}
            </Stack>
          )
        })}
        {motivoOcupado && relato.redes.length > 0 && <Text size="xs" c="dimmed">{motivoOcupado}</Text>}
      </Stack>

      {blocoDaRedePadrao}

      <Stack gap="xs">
        <Text size="sm" fw={600}>Adicionar rede</Text>
        <Group gap="sm">
          <Button variant="default" leftSection={<IconRefresh size={16} />}
            loading={enviando} disabled={ativa}
            onClick={() => agir(() => pedirWifi(endereco, { acao: 'escanear' }))}>
            Procurar redes
          </Button>
          {relato.scan_em != null && (
            <Text size="xs" c="dimmed">
              {`Última busca: ${new Date(relato.scan_em * 1000).toLocaleString('pt-BR')}`}
            </Text>
          )}
        </Group>
        <EscolherRede
          redes={relato.scan ?? []}
          salvas={salvas}
          escolha={escolha}
          onChange={setEscolha}
          disabled={enviando || ativa}
        />
        <Group align="flex-end" gap="sm">
          <PasswordInput
            label="Senha do Wi-Fi"
            description="Vazia para rede aberta. Vai ao robô e é apagada do console quando a ordem termina."
            ref={senhaNova.ref}
            onChange={senhaNova.aoMudar}
            disabled={enviando || ativa}
            autoComplete="new-password"
            w={260}
          />
          <Button loading={enviando} disabled={ativa || !!problema} onClick={salvarNova}>
            Gravar no robô
          </Button>
        </Group>
        {escolha.ssid && problema && <Text size="xs" c="red.7">{problema}</Text>}
      </Stack>

      <Modal
        opened={modo.tipo === 'trocar'}
        onClose={() => { senhaTroca.tirar(); setModo({ tipo: 'nada' }) }}
        title={modo.tipo === 'trocar' ? `Trocar a senha de ${modo.rede.ssid}` : ''}
      >
        {modo.tipo === 'trocar' && (
          <Stack gap="sm">
            {modo.rede.em_uso && (
              <Callout variant="warning" title="O robô está nesta rede agora">
                {AVISO_TROCA_EM_USO}
              </Callout>
            )}
            <PasswordInput
              label="Senha nova"
              ref={senhaTroca.ref}
              onChange={senhaTroca.aoMudar}
              autoComplete="new-password"
              data-autofocus
            />
            {senhaTroca.bytesDaSenha > 63 && (
              <Text size="xs" c="red.7">A senha passa de 63 bytes, e o robô não aceita.</Text>
            )}
            <Group justify="flex-end">
              <Button variant="default" onClick={() => { senhaTroca.tirar(); setModo({ tipo: 'nada' }) }}>
                Cancelar
              </Button>
              <Button disabled={senhaTroca.bytesDaSenha > 63} onClick={() => confirmarTroca(modo.rede)}>
                Enviar ao robô
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      <Modal
        opened={modo.tipo === 'remover'}
        onClose={() => setModo({ tipo: 'nada' })}
        title={modo.tipo === 'remover' ? `Remover ${modo.rede.ssid}` : ''}
      >
        {modo.tipo === 'remover' && (
          <Stack gap="sm">
            <Text size="sm">
              O robô esquece a rede <strong>{modo.rede.ssid}</strong> e a senha dela.
            </Text>
            {modo.rede.em_uso && (
              <Callout variant="warning" title="O robô está nesta rede agora">
                Ele continua conectado nela até cair, e depois escolhe entre as redes que sobrarem.
              </Callout>
            )}
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setModo({ tipo: 'nada' })}>Cancelar</Button>
              <Button color="red" onClick={() => confirmarRemocao(modo.rede)}>Remover rede</Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  )
}
